import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from './api';

export type Me = {
  id: number;
  email: string;
  name: string | null;
  avatar_url: string | null;
  org_id: number;
  org_name: string | null;
  role_code: string;
  totp_enabled: boolean;
};

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/auth/me'), retry: false, staleTime: 5 * 60_000 });
}

/** Any 401 from the API drops the cached user, so the guard sends the browser to sign-in. */
export function useSignedOutListener() {
  const qc = useQueryClient();
  useEffect(() => {
    const drop = () => qc.setQueryData(['me'], null);
    window.addEventListener('scamp:signed-out', drop);
    return () => window.removeEventListener('scamp:signed-out', drop);
  }, [qc]);
}

export type SignInResult = { ok: true; is_new_user?: boolean } | { ok: false; step: '2fa' };

export const session = {
  password: (email: string, password: string, remember: boolean) =>
    api<SignInResult>('/session/login', { body: { email, password, remember } }),
  sendPin: (email: string) => api<{ ok: true }>('/session/pin', { body: { email } }),
  pin: (email: string, pin: string) => api<SignInResult>('/session/pin/verify', { body: { email, pin } }),
  twoFactor: (code: string) => api<SignInResult>('/session/2fa', { body: { code } }),
  logout: () => api('/session/logout', { method: 'POST' }),
  oauthUrl: (provider: 'google' | 'github', next: string) =>
    `/api/v2/session/oauth/${provider}?next=${encodeURIComponent(next)}`,
};
